/**
 * 增量 SVG 渲染器（engine 层，学科/实验无关）：把 `LabObject[]` 按
 * `eom/equipment-registry.ts` 的几何模板画成真实 SVG DOM。
 *
 * 放在 `scene/` 而不是照抄 chrome-chemistry 的 `animation/` 目录——`scene-manager.ts`
 * 已经是空间/布局的家，渲染器与它同目录职责更一致（chrome-chemistry 自己的研究也承认
 * `animation/` 是历史演进留下的目录不一致，不是刻意的最佳实践）。
 *
 * `render()` 全量重建（首次挂载/reset 用），`sync()` 增量 patch（每帧/每次状态变化用）——
 * 照抄 chrome-chemistry svg-layer.ts 的教训：全量 replaceChildren 会丢 setPointerCapture
 * 绑定、丢 CSS 过渡，拖拽场景下会掉帧。
 */

import { getEquipmentTemplate, resolveReactive, type Shape } from "../eom/equipment-registry.js";
import type { LabObject } from "../eom/lab-object.js";

/** 导出供 scene-manager.ts 复用——追加"渲染后才挂上"的静态覆盖层元素（如放置区）时要用同一命名空间。 */
export const SVG_NS = "http://www.w3.org/2000/svg";

const SHAPE_TAG: Record<Shape["kind"], keyof SVGElementTagNameMap> = {
  rect: "rect",
  circle: "circle",
  line: "line",
  text: "text",
  path: "path",
};

function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  return node;
}

function applyShape(node: SVGElement, shape: Shape, state: Record<string, unknown>): void {
  switch (shape.kind) {
    case "rect": {
      const rect = node as SVGRectElement;
      rect.setAttribute("x", String(resolveReactive(shape.x, state)));
      rect.setAttribute("y", String(resolveReactive(shape.y, state)));
      rect.setAttribute("width", String(resolveReactive(shape.width, state)));
      rect.setAttribute("height", String(resolveReactive(shape.height, state)));
      if (shape.rx != null) rect.setAttribute("rx", String(shape.rx));
      break;
    }
    case "circle": {
      const circle = node as SVGCircleElement;
      circle.setAttribute("cx", String(resolveReactive(shape.cx, state)));
      circle.setAttribute("cy", String(resolveReactive(shape.cy, state)));
      circle.setAttribute("r", String(resolveReactive(shape.r, state)));
      break;
    }
    case "line": {
      const line = node as SVGLineElement;
      line.setAttribute("x1", String(resolveReactive(shape.x1, state)));
      line.setAttribute("y1", String(resolveReactive(shape.y1, state)));
      line.setAttribute("x2", String(resolveReactive(shape.x2, state)));
      line.setAttribute("y2", String(resolveReactive(shape.y2, state)));
      if (shape.markerEnd) line.setAttribute("marker-end", shape.markerEnd);
      break;
    }
    case "text": {
      const text = node as SVGTextElement;
      text.setAttribute("x", String(resolveReactive(shape.x, state)));
      text.setAttribute("y", String(resolveReactive(shape.y, state)));
      if (shape.fontSize != null) text.setAttribute("font-size", String(shape.fontSize));
      if (shape.anchor) text.setAttribute("text-anchor", shape.anchor);
      text.textContent = String(resolveReactive(shape.text, state));
      break;
    }
    case "path": {
      const path = node as SVGPathElement;
      path.setAttribute("d", String(resolveReactive(shape.d, state)));
      break;
    }
  }
  if (shape.fill != null) node.setAttribute("fill", String(resolveReactive(shape.fill, state)));
  if (shape.stroke != null) node.setAttribute("stroke", String(resolveReactive(shape.stroke, state)));
  if (shape.strokeWidth != null) node.setAttribute("stroke-width", String(resolveReactive(shape.strokeWidth, state)));
  const opacity = shape.opacity != null ? resolveReactive(shape.opacity, state) : undefined;
  node.setAttribute("opacity", String(opacity ?? 1));
  const visible = shape.visible != null ? resolveReactive(shape.visible, state) : true;
  node.style.display = visible ? "" : "none";
  if (shape.rotate != null) {
    node.setAttribute("transform", `rotate(${resolveReactive(shape.rotate, state)})`);
  }
}

function buildGroup(object: LabObject): SVGGElement {
  const group = el("g", {
    "data-object-id": object.id,
    "data-object-type": object.type,
  });
  group.setAttribute("transform", `translate(${object.pose.x} ${object.pose.y})`);
  const template = getEquipmentTemplate(object.type);
  if (!template) return group;
  template.geometry.forEach((shape, index) => {
    const node = el(SHAPE_TAG[shape.kind]);
    node.setAttribute("data-shape-index", String(index));
    if (shape.handle) node.setAttribute("data-handle", shape.handle);
    applyShape(node, shape, object.state);
    group.appendChild(node);
  });
  return group;
}

/** 共享渐变定义（拟物质感基础设施）：器材几何模板里用 `fill: "url(#pe-grad-metal)"` 之类的引用即可，
 * 不需要各自重复定义 `<linearGradient>`。只在 `render()`(全量重建)时插入一次——`sync()` 增量 patch
 * 不碰 svg 的其他子节点，defs 插入一次后能一直存活到下次 render()。 */
function buildDefs(): SVGDefsElement {
  const defs = el("defs");
  const metal = document.createElementNS(SVG_NS, "linearGradient") as SVGLinearGradientElement;
  metal.setAttribute("id", "pe-grad-metal");
  metal.setAttribute("x1", "0%");
  metal.setAttribute("y1", "0%");
  metal.setAttribute("x2", "0%");
  metal.setAttribute("y2", "100%");
  for (const [offset, color] of [
    ["0%", "#eef1f5"],
    ["50%", "#c7ced7"],
    ["100%", "#8f97a3"],
  ] as const) {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    metal.appendChild(stop);
  }
  defs.appendChild(metal);

  const wood = document.createElementNS(SVG_NS, "linearGradient") as SVGLinearGradientElement;
  wood.setAttribute("id", "pe-grad-wood");
  wood.setAttribute("x1", "0%");
  wood.setAttribute("y1", "0%");
  wood.setAttribute("x2", "100%");
  wood.setAttribute("y2", "100%");
  for (const [offset, color] of [
    ["0%", "#e0a85f"],
    ["55%", "#c1863f"],
    ["100%", "#8a5a2b"],
  ] as const) {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    wood.appendChild(stop);
  }
  defs.appendChild(wood);

  // 烛焰渐变：核心亮黄、向外过渡到橙、边缘收成深橙红——比纯色块更接近真实火焰的层次感。
  // 小孔成像的倒立像（equipment-registry.ts SCREEN 模板）复用同一份定义，因为像本身就是
  // 烛焰自身发出的光，颜色理应与烛焰一致，只是方向倒转、亮度略暗。
  const flame = document.createElementNS(SVG_NS, "radialGradient") as SVGRadialGradientElement;
  flame.setAttribute("id", "pe-grad-flame");
  flame.setAttribute("cx", "50%");
  flame.setAttribute("cy", "70%");
  flame.setAttribute("r", "65%");
  for (const [offset, color] of [
    ["0%", "#fff7d6"],
    ["40%", "#fde68a"],
    ["75%", "#fb923c"],
    ["100%", "#c2410c"],
  ] as const) {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    flame.appendChild(stop);
  }
  defs.appendChild(flame);
  return defs;
}

export interface SceneRenderer {
  /** 全量重建：首次挂载 / reset 用。 */
  render(objects: readonly LabObject[]): void;
  /** 增量更新：已存在的分组只 patch transform + 反应式属性，不重建 DOM。 */
  sync(objects: readonly LabObject[]): void;
}

export function createSvgRenderer(svg: SVGSVGElement): SceneRenderer {
  return {
    render(objects) {
      svg.replaceChildren();
      svg.appendChild(buildDefs());
      for (const object of objects) {
        svg.appendChild(buildGroup(object));
      }
    },
    sync(objects) {
      for (const object of objects) {
        const existing = svg.querySelector<SVGGElement>(`[data-object-id="${object.id}"]`);
        if (!existing) {
          svg.appendChild(buildGroup(object));
          continue;
        }
        existing.setAttribute("transform", `translate(${object.pose.x} ${object.pose.y})`);
        const template = getEquipmentTemplate(object.type);
        if (!template) continue;
        const children = existing.children;
        template.geometry.forEach((shape, index) => {
          const node = children[index] as SVGElement | undefined;
          if (node) applyShape(node, shape, object.state);
        });
      }
    },
  };
}
